import React, { useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * Row action (⋮) menu for POS tables, rendered outside the table so a scrolling
 * table wrapper can never clip it.
 *
 * Usage:
 *   const [actionMenu, setActionMenu] = useState(null);
 *   <button data-table-action-btn onClick={(e) => setActionMenu(toggleTableActionMenu(e, row.id, { row }))}>⋮</button>
 *   <TableActionMenu menu={actionMenu} onClose={() => setActionMenu(null)}>
 *     <li className="dropdown-item">...</li>
 *   </TableActionMenu>
 */
export const toggleTableActionMenu = (event, key, extra = {}) => {
  // Read the button position now: React clears event.currentTarget once the handler
  // returns, and the state updater below runs later.
  const rect = event.currentTarget.getBoundingClientRect();
  const next = {
    ...extra,
    key,
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
  return (prev) => (prev && prev.key === key ? null : next);
};

const TableActionMenu = ({ menu, onClose, children }) => {
  // The menu is fixed to the viewport, so close it when anything moves or the user clicks away.
  useEffect(() => {
    if (!menu) return undefined;
    const handleMouseDown = (event) => {
      if (
        event.target.closest("[data-table-action-menu]") ||
        event.target.closest("[data-table-action-btn]")
      ) {
        return;
      }
      onClose();
    };
    const handleScroll = (event) => {
      if (event.target?.closest?.("[data-table-action-menu]")) return;
      onClose();
    };
    document.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [menu, onClose]);

  if (!menu || typeof document === "undefined") return null;

  // .customer-table-dropdown styles are scoped under .main-layout-pos, so portal there.
  const portalTarget = document.querySelector(".main-layout-pos") || document.body;

  return createPortal(
    // Anchor sits exactly over the ⋮ button; the existing .customer-table-dropdown CSS
    // places the menu to its left, same as the in-table dropdown did.
    <div
      className="customer-table-dropdown"
      data-table-action-menu
      style={{
        position: "fixed",
        top: menu.top,
        left: menu.left,
        width: menu.width,
        height: menu.height,
        zIndex: 1040,
      }}
      onClick={(event) => {
        // Close on the next tick so Bootstrap's document-level handlers (data-bs-toggle="modal")
        // still find the clicked item in the DOM.
        if (event.target.closest("li")) setTimeout(onClose, 0);
      }}
    >
      <div className="dropdown" style={{ position: "relative", width: "100%", height: "100%" }}>
        <ul className="dropdown-menu show" style={{ top: 0 }}>
          {children}
        </ul>
      </div>
    </div>,
    portalTarget
  );
};

export default TableActionMenu;
